import { useState, useEffect } from 'react';
import octopusInstance from '../../../../../services/octopus/octopusInstance';

/**
 * Custom hook to handle WebSocket live price subscription for an instrument.
 * @param {string} exchange - The exchange name (e.g. "NSE", "NASDAQ")
 * @param {number|string} token - The instrument token
 * @param {number|null} wsCode - The calculated exchange code for WebSocket
 * @returns {Object} { price, change, pct }
 */
export const useLivePrice = (exchange, token, wsCode) => {
  const [wsData, setWsData] = useState({ price: 0, change: 0, pct: 0 });

  // When the tab becomes visible after idle, the OctopusManager destroys
  // the old Octopus instance and creates a new one. Unlike MarketWatch
  // (whose useEffect re-runs because its `instrument` object reference
  // changes via Redux), our primitive dependencies (exchange, token, wsCode)
  // never change — so the effect never re-runs and the subscription is
  // permanently lost on the destroyed instance.
  //
  // Fix: bump a counter on visibilitychange to force re-subscription.
  const [subKey, setSubKey] = useState(0);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        setSubKey((prev) => prev + 1);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  useEffect(() => {
    // Safely ignore invalid tokens (like 0) or missing wsCode,
    // without breaking the UI render.
    if (token == null || wsCode === null) return;

    const handler = octopusInstance.wsHandler({
      messageType: 'CompactMarketDataMessage',
      subscriptionLocation: `intl-card-${exchange}-${token}`,
      payload: {
        exchangeCode: wsCode,
        instrumentToken: Number(token),
      },
    });

    handler.subscribe(({ msg }) => {
      const price = msg.ltp;
      if (price !== undefined) {
        setWsData({
          price,
          change: msg.absoluteChange ?? 0,
          pct: msg.percentChange ?? 0,
        });
      }
    });

    return () => handler.unsubscribe();
    // subKey forces re-subscription after tab-visibility reconnection
  }, [exchange, token, wsCode, subKey]);

  return wsData;
};
